// Money → Transactions (spec 044 D50–D58): which tab a ledger row belongs to, the
// periods, the page reader's cursor rule, and the tab counts and card totals. Pure — no
// React, no Firestore — so Jest runs it.
import { MONTHS } from "./queuePresentation";

// The spec's kinds table, first match wins. Rows written before the cutover have `status`
// and `type` but often no `transactionType` (465 lesson and refund rows as late as
// 21 Sep 2026), so those two are read first. No row in staging or production carries both a
// `status` and a `type` (counted 2026-09-28), so reading `status` first misfiles none.
export const kindOf = (t = {}) => {
  if (t.status === "created") return "checkout";
  if (t.status === "succeeded") return "topup";
  if (t.type === "payout") return "tutorPay";
  if (t.transactionType === "adminAdjustment") return "adjustment";
  if (t.transactionType === "referralReward") return "referral";
  if (t.type === "debit" || t.type === "refund") return "lesson";
  return "other";
};

export const TABS = [
  { key: "all", label: "All" },
  { key: "topups", label: "Top-ups" },
  { key: "lessons", label: "Lessons & refunds" },
  { key: "adjustments", label: "Adjustments" },
  { key: "referrals", label: "Referral rewards" },
  { key: "donations", label: "Donations" }, // admins only (D57)
];

// R4 / D59: the tutor's late-cancel pay rows stay off the page (the owner's pick); showing
// them under All is this one flag.
export const SHOW_TUTOR_PAY = false;

// R3: "other" rows (0 sessions) are listed under All only.
const TAB_KINDS = {
  all: ["topup", "lesson", "adjustment", "referral", "other"],
  topups: ["topup"],
  lessons: ["lesson"],
  adjustments: ["adjustment"],
  referrals: ["referral"],
};

// Whether a row of `kind` is listed on `tab`. Checkout rows join All and Top-ups only
// when switched on (D54).
export const visibleIn = (tab, kind, { showCheckout = false } = {}) => {
  if (kind === "checkout") return showCheckout && (tab === "all" || tab === "topups");
  if (kind === "tutorPay") return SHOW_TUTOR_PAY && tab === "all";
  return (TAB_KINDS[tab] || []).includes(kind);
};

export const PERIODS = [
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "last3", label: "Last 3 months" },
  { key: "last12", label: "Last 12 months" },
];

const MONTHS_BACK = { thisMonth: 0, lastMonth: 1, last3: 2, last12: 11 };

// R1: calendar months in the viewer's time zone; `to` is exclusive.
export const periodRange = (key, now = new Date()) => {
  const back = MONTHS_BACK[key];
  if (back === undefined) throw new Error(`periodRange: unknown period ${key}`);
  const y = now.getFullYear();
  const m = now.getMonth();
  return { from: new Date(y, m - back, 1), to: key === "lastMonth" ? new Date(y, m, 1) : new Date(y, m + 1, 1) };
};

// The last day a period shows: today while the period runs on, else its own last day
// (day 0 of `to`'s month, which no clock change can move).
export const periodEnd = ({ from, to }, now = new Date()) =>
  to > now ? now : new Date(to.getFullYear(), to.getMonth(), 0);

// "1 – 28 Sep 2026", "1 Jul – 28 Sep 2026", "1 Oct 2025 – 28 Sep 2026".
export const periodLabel = (range, now = new Date()) => {
  const { from } = range;
  const end = periodEnd(range, now);
  const full = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  if (from.getFullYear() !== end.getFullYear()) return `${full(from)} – ${full(end)}`;
  if (from.getMonth() !== end.getMonth()) return `${from.getDate()} ${MONTHS[from.getMonth()]} – ${full(end)}`;
  if (from.getDate() === end.getDate()) return full(end);
  return `${from.getDate()} – ${full(end)}`;
};

// R5: read batches until pageSize + 1 rows survive `isVisible`, or the reader runs dry.
// `fetchBatch(after)` resolves to { docs: [{ row, snap }], full } — `snap` is what the
// next read starts after. The next page starts after the last row shown, so hidden rows
// past it are simply read again and skipped.
export const collectPage = async ({ fetchBatch, isVisible, start = null, pageSize = 25 }) => {
  const shown = [];
  let after = start;
  for (;;) {
    const { docs, full } = await fetchBatch(after);
    for (const d of docs) {
      after = d.snap;
      if (!isVisible(d.row)) continue;
      if (shown.length === pageSize) return { rows: shown.map((s) => s.row), next: shown[pageSize - 1].snap, hasMore: true };
      shown.push(d);
    }
    if (!full || docs.length === 0) return { rows: shown.map((s) => s.row), next: null, hasMore: false };
  }
};

const total = (rows, field = "amount") => rows.reduce((s, t) => s + (Number(t[field]) || 0), 0);

// R6: an adjustment's sessions, split by the way they moved.
export const adjustmentTotals = (rows = []) => ({
  n: rows.length,
  took: total(rows.filter((t) => t.type === "debit")),
  gave: total(rows.filter((t) => t.type === "refund")),
});

// R7: the aggregates shape from one person's rows — the same filters the service's
// aggregation queries run for a whole period.
export const aggregateRows = (rows = []) => {
  const pick = (test) => rows.filter(test);
  const paid = pick((t) => t.status === "succeeded");
  const debit = pick((t) => t.type === "debit");
  const refund = pick((t) => t.type === "refund");
  const referral = pick((t) => t.transactionType === "referralReward");
  return {
    all: rows.length,
    paid: { n: paid.length, gbp: total(paid), sessions: total(paid, "hours") },
    checkout: pick((t) => t.status === "created").length,
    debit: { n: debit.length, sessions: total(debit) },
    refund: { n: refund.length, sessions: total(refund) },
    tutorPay: pick((t) => t.type === "payout").length,
    referral: { n: referral.length, sessions: total(referral) },
    adjustments: adjustmentTotals(pick((t) => t.transactionType === "adminAdjustment")),
    donations: null,
  };
};

// A number that failed is null, and so is anything built on it: it reads "—" (D36).
const add = (...xs) => (xs.some((x) => x == null) ? null : xs.reduce((s, x) => s + x, 0));
const neg = (x) => (x == null ? null : -x);

export const tabCounts = (a, { showCheckout = false } = {}) => ({
  all: add(a.all, showCheckout ? 0 : neg(a.checkout), SHOW_TUTOR_PAY ? 0 : neg(a.tutorPay)),
  topups: add(a.paid?.n, showCheckout ? a.checkout : 0),
  lessons: add(a.debit?.n, a.refund?.n, neg(a.adjustments?.n), neg(a.referral?.n)),
  adjustments: a.adjustments?.n ?? null,
  referrals: a.referral?.n ?? null,
  donations: a.donations ?? null,
});

// D52: adjustments and referral rewards have their own tabs, so they count in neither
// sessions used nor sessions refunded.
export const cardTotals = (a) => ({
  collected: a.paid?.gbp ?? null,
  sold: a.paid?.sessions ?? null,
  used: add(a.debit?.sessions, neg(a.adjustments?.took)),
  refunded: add(a.refund?.sessions, neg(a.adjustments?.gave), neg(a.referral?.sessions)),
});
